import React from "react";
import { motion } from "framer-motion";
import { ChefHat, MapPin, ShieldCheck, Star, Truck } from "lucide-react";
import { Button } from "./ui/button";

const ServiceArea = () => {
  const handleOfferClick = () => {
    window.open(
      "https://wa.me/6287884243038?text=Halo%2C%20saya%20mau%20penawaran%20spesial%20Aqiqah",
      "_blank"
    );
  };

  return (
    <section className="relative py-20 px-6 bg-gradient-to-br from-[#FFF5F2] via-[#FFF9F7] to-[#FFFFFF] overflow-hidden">
      {/* 🌸 Decorative background */}
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M12 1L12 23M1 12L23 12' stroke='%23FF8A65' stroke-width='0.4'/%3E%3C/svg%3E")`,
          backgroundRepeat: "repeat",
        }}
      />

      {/* 🌈 Gradient blobs */}
      <div className="absolute top-0 left-0 w-80 h-80 bg-[#FFC4B2]/40 blur-[120px] rounded-full opacity-60" />
      <div className="absolute bottom-0 right-0 w-80 h-80 bg-[#58B09C]/30 blur-[100px] rounded-full opacity-50" />

      <div className="relative z-10 max-w-7xl mx-auto flex flex-col-reverse lg:grid lg:grid-cols-2 gap-12 items-center">
        {/* 🔸 Left Text Section */}
        <motion.div
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="w-full"
        >
          {/* Badge */}
          <div className="flex items-center gap-2 mb-5">
            <MapPin className="text-[#FF8A65] w-5 h-5" />
            <span className="text-[#FF8A65] font-semibold uppercase tracking-wider text-sm md:text-base">
              Jangkauan Luas & Cepat
            </span>
          </div>

          {/* Title */}
          <h2
            className="text-3xl md:text-5xl font-extrabold text-[#333] mb-6 leading-tight"
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            Melayani <span className="text-[#FF8A65]">Jabodetabek</span> &
            Sekitarnya
          </h2>

          {/* Description */}
          <p
            className="text-[#6F6F6F] text-lg leading-relaxed mb-8"
            style={{ fontFamily: "Roboto, sans-serif" }}
          >
            Dapur kami dikelola oleh{" "}
            <span className="font-semibold text-[#333]">
              chef profesional berpengalaman
            </span>{" "}
            dengan standar higienitas tinggi. Kami siap mengantar pesanan Anda
            dengan pelayanan cepat dan terpercaya ke seluruh wilayah{" "}
            <span className="text-[#FF8A65] font-medium">
              Jakarta, Bogor, Depok, Tangerang, dan Bekasi.
            </span>
          </p>

          {/* 🔹 Features List */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-10">
            {/* Feature 1 */}
            <div className="flex items-start gap-4">
              <div className="bg-[#FFF5F2] p-3 rounded-xl shrink-0 shadow-sm border border-[#FFD8C8]/60">
                <ChefHat className="w-7 h-7 text-[#FF8A65]" />
              </div>
              <div>
                <h4 className="text-lg font-semibold text-[#333]">
                  Chef Profesional
                </h4>
                <p className="text-[#777] text-sm leading-snug">
                  Pengolahan higienis, rasa lezat, dan konsistensi cita rasa.
                </p>
              </div>
            </div>

            {/* Feature 2 */}
            <div className="flex items-start gap-4">
              <div className="bg-[#FFF5F2] p-3 rounded-xl shrink-0 shadow-sm border border-[#FFD8C8]/60">
                <Truck className="w-7 h-7 text-[#FF8A65]" />
              </div>
              <div>
                <h4 className="text-lg font-semibold text-[#333]">
                  Pengiriman Cepat
                </h4>
                <p className="text-[#777] text-sm leading-snug">
                  Armada siap antar pesanan Anda tepat waktu & aman.
                </p>
              </div>
            </div>
          </div>

          {/* Trust Metrics Badges */}
          <div className="flex flex-wrap gap-4 mb-8">
            <div className="flex items-center gap-2 bg-[#FFF5F2] px-4 py-2 rounded-full text-[#FF8A65] text-sm font-semibold shadow-sm border border-[#FFD8C8]/60">
              <ShieldCheck className="w-4 h-4" /> 100% Halal Certified
            </div>
            <div className="flex items-center gap-2 bg-[#FFF5F2] px-4 py-2 rounded-full text-[#58B09C] text-sm font-semibold shadow-sm border border-[#C4E6DD]/60">
              <Star className="w-4 h-4" /> 4.9★ Kepuasan Pelanggan
            </div>
          </div>

          {/* CTA Button */}
          <Button
            onClick={handleOfferClick}
            className="bg-gradient-to-r from-[#FF8A65] to-[#FF6B4A] hover:from-[#FF7C50] hover:to-[#FF5A36] text-white px-8 py-6 rounded-xl shadow-lg text-base md:text-lg font-bold transition-all hover:scale-105 active:scale-95"
          >
            🎁 Dapatkan Penawaran Spesial
          </Button>
        </motion.div>

        {/* 🔹 Right Image Section */}
        <motion.div
          initial={{ opacity: 0, x: 40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="relative w-full"
        >
          <div className="aspect-square rounded-3xl overflow-hidden shadow-[0_20px_60px_rgba(255,138,101,0.15)] border border-[#FFE3D8]/80">
            <img
              alt="Chef professional cooking aqiqah menu"
              className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-700"
              src="https://images.unsplash.com/photo-1692275294193-656d9dca69ae"
            />
          </div>

          {/* Floating Badge */}
          <div className="absolute -bottom-6 -left-6 bg-white p-4 md:p-6 rounded-2xl shadow-2xl border-l-4 border-[#FF8A65]">
            <p className="text-2xl font-extrabold text-[#333] leading-none">
              100%
            </p>
            <p className="text-sm text-[#8A8A8A] font-medium">
              Kepuasan Pelanggan
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ServiceArea;
